import type {
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  ISeriesPrimitive,
  PrimitivePaneViewZOrder,
  SeriesAttachedParameter,
  Time,
} from "lightweight-charts";

export interface TriggeredMarkerPrimitiveOptions {
  time: Time;
  price: number;
  /** e.g. "Triggered at 87.16" */
  labelText: string;
}

interface MarkerCoordinates {
  x: number;
  y: number;
}

const BOX_PADDING_X = 14;
const BOX_PADDING_Y = 10;
const BOX_RADIUS = 6;
/** Space between bottom of trigger icon and top of label */
const MARKER_TO_BOX_GAP = 8;
const BOX_MIN_WIDTH = 160;
const LINE_HEIGHT = 18;
const CONNECTOR_COLOR = "rgba(255, 255, 255, 0.4)";

/** Public asset; 100×100 source, drawn smaller on the chart */
const TRIGGER_MARKER_URL = "/triggerImage_100x100.png";
const MARKER_SIZE_PX = 35;

/** Vertical space below the marker price point needed for the label callout */
export const TRIGGER_MARKER_LABEL_SPACE_PX =
  MARKER_SIZE_PX / 2 + MARKER_TO_BOX_GAP + BOX_PADDING_Y * 2 + LINE_HEIGHT + 8;

let sharedTriggerImage: HTMLImageElement | null = null;
let loadStarted = false;
const pendingOnReady: Array<() => void> = [];

function requestTriggerImage(onReady: () => void) {
  if (sharedTriggerImage) {
    onReady();
    return;
  }
  pendingOnReady.push(onReady);
  if (loadStarted) {
    return;
  }
  loadStarted = true;
  const img = new Image();
  img.onload = () => {
    sharedTriggerImage = img;
    const pending = pendingOnReady.splice(0, pendingOnReady.length);
    for (const fn of pending) {
      fn();
    }
  };
  img.onerror = () => {
    loadStarted = false;
    sharedTriggerImage = null;
    pendingOnReady.length = 0;
  };
  img.src = TRIGGER_MARKER_URL;
}

const drawRoundedRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) => {
  const safeRadius = Math.min(radius, width / 2, height / 2);

  ctx.beginPath();
  ctx.moveTo(x + safeRadius, y);
  ctx.lineTo(x + width - safeRadius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
  ctx.lineTo(x + width, y + height - safeRadius);
  ctx.quadraticCurveTo(
    x + width,
    y + height,
    x + width - safeRadius,
    y + height
  );
  ctx.lineTo(x + safeRadius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
  ctx.lineTo(x, y + safeRadius);
  ctx.quadraticCurveTo(x, y, x + safeRadius, y);
  ctx.closePath();
};

class TriggeredMarkerPaneRenderer implements IPrimitivePaneRenderer {
  constructor(
    private readonly coordinates: MarkerCoordinates,
    private readonly options: TriggeredMarkerPrimitiveOptions
  ) {}

  draw(target: Parameters<IPrimitivePaneRenderer["draw"]>[0]) {
    const img = sharedTriggerImage;

    target.useMediaCoordinateSpace(({ context: ctx, mediaSize }) => {
      const { x } = this.coordinates;
      const half = MARKER_SIZE_PX / 2;
      const boxHeight = BOX_PADDING_Y * 2 + LINE_HEIGHT;
      // A trigger price outside the visible range (e.g. the price jumped far
      // past the alert price) is pinned to the pane edge instead of vanishing.
      const minY = half + 4;
      const maxY = mediaSize.height - half - MARKER_TO_BOX_GAP - boxHeight - 8;
      const rawY = this.coordinates.y;
      const below = rawY > maxY;
      const above = rawY < minY;
      const y = Math.min(Math.max(rawY, minY), Math.max(minY, maxY));
      const label = below
        ? `${this.options.labelText} ↓`
        : above
          ? `${this.options.labelText} ↑`
          : this.options.labelText;
      const markerBottomY = y + half;
      const markVisible =
        x + half >= 0 && x - half <= mediaSize.width;

      if (!markVisible) {
        return;
      }

      ctx.save();
      ctx.font =
        '14px Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      const textWidth = ctx.measureText(label).width;
      const boxWidth = Math.max(BOX_MIN_WIDTH, textWidth + BOX_PADDING_X * 2);

      let boxX = x - boxWidth / 2;
      boxX = Math.max(8, Math.min(boxX, mediaSize.width - boxWidth - 8));

      // In the lower half the label goes above the marker, clear of the
      // chart's bottom-left attribution logo; at the bottom edge it stays
      // below so the ↓ points past the pane.
      const boxAbove =
        !below && y > mediaSize.height / 2 && y - half - MARKER_TO_BOX_GAP - boxHeight >= 8;
      const boxY = boxAbove
        ? y - half - MARKER_TO_BOX_GAP - boxHeight
        : Math.max(8, markerBottomY + MARKER_TO_BOX_GAP);

      ctx.strokeStyle = CONNECTOR_COLOR;
      ctx.lineWidth = 1;
      if (boxAbove) {
        ctx.beginPath();
        ctx.moveTo(x, boxY + boxHeight);
        ctx.lineTo(x, y - half);
        ctx.stroke();
      } else if (boxY > markerBottomY) {
        ctx.beginPath();
        ctx.moveTo(x, markerBottomY);
        ctx.lineTo(x, boxY);
        ctx.stroke();
      }

      if (img) {
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(
          img,
          x - half,
          y - half,
          MARKER_SIZE_PX,
          MARKER_SIZE_PX
        );
      }

      ctx.fillStyle = "#111318";
      ctx.strokeStyle = "rgba(255, 255, 255, 0.08)";
      ctx.lineWidth = 1;
      drawRoundedRect(ctx, boxX, boxY, boxWidth, boxHeight, BOX_RADIUS);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = "#f5f5f5";
      ctx.textBaseline = "top";
      ctx.fillText(label, boxX + BOX_PADDING_X, boxY + BOX_PADDING_Y);

      ctx.restore();
    });
  }
}

class TriggeredMarkerPaneView implements IPrimitivePaneView {
  constructor(private readonly marker: TriggeredMarkerPrimitive) {}

  zOrder(): PrimitivePaneViewZOrder {
    return "top";
  }

  renderer(): IPrimitivePaneRenderer | null {
    const coordinates = this.marker.coordinates();

    if (!coordinates) {
      return null;
    }

    return new TriggeredMarkerPaneRenderer(coordinates, this.marker.options);
  }
}

export class TriggeredMarkerPrimitive implements ISeriesPrimitive<Time> {
  private attachedParams: SeriesAttachedParameter<Time> | null = null;
  private readonly paneViewsList: readonly IPrimitivePaneView[] = [
    new TriggeredMarkerPaneView(this),
  ];

  constructor(readonly options: TriggeredMarkerPrimitiveOptions) {}

  attached(param: SeriesAttachedParameter<Time>) {
    this.attachedParams = param;
    requestTriggerImage(() => {
      this.attachedParams?.requestUpdate();
    });
    param.requestUpdate();
  }

  detached() {
    this.attachedParams = null;
  }

  paneViews(): readonly IPrimitivePaneView[] {
    return this.paneViewsList;
  }

  coordinates(): MarkerCoordinates | null {
    if (!this.attachedParams) {
      return null;
    }

    const x = this.attachedParams.chart
      .timeScale()
      .timeToCoordinate(this.options.time);
    const y = this.attachedParams.series.priceToCoordinate(this.options.price);

    if (x === null || y === null) {
      return null;
    }

    return { x, y };
  }
}
